import { Download } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Tab, Tabs, Typography } from "@mui/material";
import { Buffer } from "buffer";
import dayjs from "dayjs";
import saveAs from "file-saver";
import { MRT_ColumnFiltersState } from "material-react-table";
import moment from "moment";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import useFetch from "src/Components/Common/useFetch";
import CustomToast from "src/Components/CustomToast";
import ReactTable, { ColDef } from "src/Components/ReactTable/ReactTable";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { filterDateBetweenStartAndEndDate } from "src/Utils/DateUtils";
import {
  trackInvoiceRejectionDownloaded,
  trackInvoiceRejectionTabSwitched,
  trackInvoiceRejectionTakeActionClicked,
  trackInvoiceRejectionViewed,
} from "src/Utils/Mixpanel/VendorPortalEvents";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";
import { uiLoggerNamesVendorPortal } from "src/Utils/Recon/UiLogger/Constants";
import { ColorToggleButton } from "../PartnerCommunication/AuditCommunication/Common/Components";
import ErrorPage from "../PartnerCommunication/MsmePartnerPortal/ErrorPage";
import { LoadingPage } from "../PartnerCommunication/MsmePartnerPortal/PartnerPortal.common";
import { useVendorPortal } from "./VendorPortalLayout";
import VendorPortalRejectedInvoicesResolveModal from "./VendorPortalRejectedInvoicesResolveModal";

export const RejectedInvoicesQuickFilterOptions = {
  All: "All",
  InvoiceCopyRequired: "Invoice Copy Required",
  NewInvoiceRequired: "New Invoice Required",
} as const;

export const VendorResolutionStatus = {
  Unassigned: "Unassigned",
  InProgress: "In-Progress",
  PendingApproval: "Pending Approval",
  InvoiceProcessing: "Invoice Processing",
  RejectedAgain: "Rejected Again",
  Posted: "Posted",
};
export const IssueCategorizationOptions = {
  UNRECONCILE_VALIDATION: "Unreconcile - Validation",
  UNRECONCILE_REJECTIONS: "Unreconcile - Rejections",
  MORE_THAN_20: "More than 20",
  WRONG_GST_NUMBER: "Wrong GST Number",
  OTHER_ISSUE: "Other issue",
  ANY_TECHNICAL_ISSUE: "Any Technical issue",
  POD_CREATION: "POD Creation",
  INCORRECT_WRONG_INVOICE_ATTACHED: "Incorrect/Wrong invoice attached",
  INVOICE_COPY_NOT_LEGIBLE_VISIBLE: "Invoice copy not legible/visible",
  INCOMPLETE_PARTIAL_INVOICE_UPLOADED: "Incomplete/partial Invoice uploaded",
  INVOICE_COPY_MISSING: "Invoice copy missing",
  INWARDING_ISSUE: "Inwarding issue",
  WRONG_INWARDING_WRONG_VENDOR_SELECTED: "Wrong Inwarding / Wrong Vendor Selected",
  INVOICE_NO_MAX_16_CHARACTER: "Invoice No should be maximum 16 character",
  GRN_MADE_WITHOUT_TAX: "GRN Made Without Tax",
  GRN_NO_ALREADY_EXIST: "GRN No Already Exist",
  GRN_MADE_LESS: "GRN Made Less",
  DUPLICATE_INVOICE: "Duplicate invoice",
  INWARDING_GSTN_MISMATCH: "Inwarding GSTN Mismatch",
  INCORRECT_WRONG_BILLING_ADDRESS: "Incorrect /wrong billing address",
  KYC_PENDING: "KYC Pending",
  QR_CODE_MISSING: "QR Code Missing",
  ISCAN: "Iscan",
  BUYER_GSTIN_MISMATCH: "Buyer GSTIN Mismatch",
  TOBACCO_TAX_ISSUE: "Tobacco Tax Issue",
  VENDOR_IS_INACTIVE: "vendor is in inactive",
  VENDOR_NOT_FOUND: "Vendor not found",
  VENDOR_RELATED_ISSUES: "Vendor Related Issues",
  ZOHO_ISSUES: "Zoho Issues",
} as const;

export const IssueCategorizationDescriptions = {
  "Unreconcile - Validation": "",
  "Unreconcile - Rejections": "",
  "More than 20": "",
  "Wrong GST Number": "The GST present in the uploaded invoice is wrong",
  "Other issue": "",
  "Any Technical issue": "",
  "POD Creation": "",
  "Incorrect/Wrong invoice attached": "The uploaded invoice is not correct",
  "Invoice copy not legible/visible": "The uploaded invoice is not clearly visible",
  "Incomplete/partial Invoice uploaded": "The uploaded invoice is not complete",
  "Invoice copy missing": "Actual invoice copy is missing",
  "Inwarding issue": "",
  "Wrong Inwarding / Wrong Vendor Selected": "",
  "Invoice No should be maximum 16 character": "The invoice number exceeds 16 characters",
  "GRN Made Without Tax": "",
  "GRN No Already Exist": "",
  "GRN Made Less": "",
  "Duplicate invoice": "The uploaded invoice already exists",
  "Inwarding GSTN Mismatch": "The GST present in the uploaded invoice doesn't match",
  "Incorrect /wrong billing address": "The billing address present in the uploaded invoice is incorrect",
  "KYC Pending": "",
  "QR code missing": "",
  Iscan: "",
} as const;

export type RejectedInvoicesQF =
  typeof RejectedInvoicesQuickFilterOptions[keyof typeof RejectedInvoicesQuickFilterOptions];

const RejectedInvoicesVendorPortal = () => {
  const { handleUnauthorizedError, uiLogsVendorPortal, isSessionReady, token } = useVendorPortal();
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [selectedQF, setSelectedQF] = useState<RejectedInvoicesQF>(RejectedInvoicesQuickFilterOptions.All);
  const rejectedInvoicesQuickFilterOptions = Object.values(RejectedInvoicesQuickFilterOptions) as RejectedInvoicesQF[];
  const [rowsDataRejectedInvoicesNonPosted, setRowsDataRejectedInvoicesNonPosted] = useState([]);
  const [storeRowsDataRejectedInvoicesNonPosted, setStoreRowsDataRejectedInvoicesNonPosted] = useState([]);
  const [rowsDataRejectedInvoicesPosted, setRowsDataRejectedInvoicesPosted] = useState([]);
  const [summaryOfVendorPortal, setSummaryOfVendorPortal] = useState(null);
  const [loader, setLoader] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openRejectedInvoicesResolveModal, setOpenRejectedInvoicesResolveModal] = useState(false);
  const selectedRowForRejectedInvoicesResolveModal = useRef(null);
  const [rejectedInvoicesResolveModalTitle, setRejectedInvoicesResolveModalTitle] = useState("");
  const [isDownloadInvoiceReport, setIsDownloadInvoiceReport] = useState(false);

  // Usetiful tags
  useEffect(() => {
    const getUnique = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

    const userId = getUnique("user");
    const name = getUnique("name");
    const actorId = getUnique("actor");

    const script = document.createElement("script");
    script.nonce = "skip-5";
    script.innerHTML = `
    window.usetifulTags = {
      userId: "${userId}",
      name: "${name}",
      ActorId: "${actorId}",
    };
  `;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const QuickFilterRows = (originalRows, selectedQF) => {
    if (selectedQF === RejectedInvoicesQuickFilterOptions.InvoiceCopyRequired) {
      // Filter rows for Invoice Copy Required
      uiLogsVendorPortal(uiLoggerNamesVendorPortal.UI_SST_IR_INVOICE_COPY_REQUIRED_CLICK.message, {
        function: uiLoggerNamesVendorPortal.UI_SST_IR_INVOICE_COPY_REQUIRED_CLICK.functionName,
      });
      return originalRows.filter((row) =>
        [
          IssueCategorizationOptions.WRONG_GST_NUMBER,
          IssueCategorizationOptions.INCORRECT_WRONG_INVOICE_ATTACHED,
          IssueCategorizationOptions.INVOICE_COPY_NOT_LEGIBLE_VISIBLE,
          IssueCategorizationOptions.INCOMPLETE_PARTIAL_INVOICE_UPLOADED,
          IssueCategorizationOptions.INVOICE_COPY_MISSING,
          IssueCategorizationOptions.INWARDING_ISSUE,
          IssueCategorizationOptions.WRONG_INWARDING_WRONG_VENDOR_SELECTED,
          IssueCategorizationOptions.INVOICE_NO_MAX_16_CHARACTER,
        ].includes(row.issueCategory)
      );
    } else if (selectedQF === RejectedInvoicesQuickFilterOptions.NewInvoiceRequired) {
      // Filter rows for New Invoice Required
      uiLogsVendorPortal(uiLoggerNamesVendorPortal.UI_SST_IR_NEW_INVOICE_REQUIRED_CLICK.message, {
        function: uiLoggerNamesVendorPortal.UI_SST_IR_NEW_INVOICE_REQUIRED_CLICK.functionName,
      });
      return originalRows.filter((row) =>
        [
          IssueCategorizationOptions.DUPLICATE_INVOICE,
          IssueCategorizationOptions.INWARDING_GSTN_MISMATCH,
          IssueCategorizationOptions.INCORRECT_WRONG_BILLING_ADDRESS,
        ].includes(row.issueCategory)
      );
    }

    // For "All", return all rows
    return originalRows;
  };

  const finalQuickFilteredRejectedInvoices = QuickFilterRows(
    tabValue === 0 ? rowsDataRejectedInvoicesNonPosted : rowsDataRejectedInvoicesPosted,
    selectedQF
  );

  const columnDefinitionRejectedInvoices = React.useMemo(
    (): ColDef<any>[] => [
      {
        header: "Issue categorization",
        accessorFn: (row) => row.issueCategory || "",
        filterVariant: "multi-select",
        size: 300,
      },
      {
        header: "Resolution Status",
        accessorFn: (row) => row.issueStatus || "", // Assuming `status` is the key in your data
        filterVariant: "multi-select",
        size: 250,
        Cell: ({ cell }) => {
          const issueStatus = cell.getValue() as string; // Get the status value
          let chipColor: "primary" | "warning" | "success" | "default" | "secondary" | "error" | "info" = "default"; // Default chip color
          if (issueStatus === VendorResolutionStatus.InProgress) chipColor = "primary";
          else if (issueStatus === VendorResolutionStatus.PendingApproval) chipColor = "warning";
          else if (issueStatus === VendorResolutionStatus.InvoiceProcessing) chipColor = "success";
          else if (issueStatus === VendorResolutionStatus.Posted) chipColor = "success";

          return <Chip label={issueStatus} size="small" color={chipColor} variant="outlined" />;
        },
      },

      {
        header: "Invoice Number",
        accessorFn: (row) => row.invoiceNumber || "",
        size: 300,
      },
      {
        header: "GRN Amount",

        accessorFn: (row) => row.grnAmount || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.grnAmount ? formatMoney(ToDineroObj(row.original?.grnAmount)) : ""}
          >
            {row.original?.grnAmount ? formatMoney(ToDineroObj(row.original?.grnAmount)) : "-"}
          </div>
        ),
      },
      {
        header: "GRN Number",

        accessorFn: (row) => row.grnNumber || "",
        size: 280,
      },
      {
        header: "GRN Date",

        accessorFn: (row) => new Date(row.grnDate || null),
        size: 350,
        filterVariant: "date-range",
        // custom filter function
        filterFn: (row, _id, filterValue, _meta) => {
          const minDate = filterValue[0] ? dayjs(filterValue[0]).toDate() : null;
          const maxDate = filterValue[1] ? dayjs(filterValue[1]).toDate() : null;
          if (minDate || maxDate) {
            return filterDateBetweenStartAndEndDate(minDate, maxDate, row.original.grnDate);
          }
        },
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">
            {row.original.grnDate ? moment(row.original.grnDate)?.format("DD-MM-YYYY") : "-"}
          </div>
        ),
      },
      {
        header: "PO Number",

        accessorFn: (row) => row.poNumber || "",
        size: 250,
      },
      {
        header: "Dependency",

        accessorFn: (row) => row.dependency || "",
        size: 250,
        filterVariant: "multi-select",
      },
      {
        header: "Category",

        accessorFn: (row) => row.category || "",
        filterVariant: "multi-select",
        filterFn: (row, columnId, filterValue) => {
          // If no filters are applied, show all rows
          if (!filterValue || filterValue.length === 0) {
            return true;
          }
          // Otherwise, match exactly
          return filterValue.includes(row.getValue(columnId));
        },
        size: 250,
      },
      {
        header: "Item Type",

        accessorFn: (row) => row.itemType || "",
        filterVariant: "multi-select",
        filterFn: (row, columnId, filterValue) => {
          // If no filters are applied, show all rows
          if (!filterValue || filterValue.length === 0) {
            return true;
          }
          // Otherwise, match exactly
          return filterValue.includes(row.getValue(columnId));
        },
        size: 250,
      },
      {
        header: "Outlet Name",
        accessorFn: (row) => row.outletName || "",
        filterVariant: "multi-select",
        size: 300,
      },
      {
        header: "City",

        accessorFn: (row) => row.city || "",
        filterVariant: "multi-select",
        size: 250,
      },
    ],
    []
  );

  useEffect(() => {
    // Only run when session is ready
    if (!isSessionReady) return;

    if (token) {
      // If token exists, use it to fetch data
      trackInvoiceRejectionViewed();
      listOpenVendorPortalInvoices(token);
      listCloseVendorPortalInvoices(token);
    } else {
      // If no token, show error
      toast.error(<CustomToast message="Session expired. Please log in again." />);
    }
  }, [isSessionReady, token]); // Depend on both isSessionReady and token

  const listOpenVendorPortalInvoices = (token) => {
    setLoader(true);
    useFetch(API_ENDPOINTS.OPEN_VENDOR_PORTAL_INVOICES.url, "GET", {
      failureMessage: API_ENDPOINTS.OPEN_VENDOR_PORTAL_INVOICES.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      thenCallBack: (response) => {
        setRowsDataRejectedInvoicesNonPosted(response.data?.invoices || []);
        setStoreRowsDataRejectedInvoicesNonPosted(response.data?.invoices || []);
        setSummaryOfVendorPortal(response.data?.summary);
        setLoader(false);
      },
      catchCallBack: (error) => {
        handleUnauthorizedError(error);
      },
    });
  };
  const listCloseVendorPortalInvoices = (token) => {
    useFetch(API_ENDPOINTS.CLOSE_VENDOR_PORTAL_INVOICES.url, "GET", {
      failureMessage: API_ENDPOINTS.CLOSE_VENDOR_PORTAL_INVOICES.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      thenCallBack: (response) => {
        setRowsDataRejectedInvoicesPosted(response.data?.invoices || []);
      },
      catchCallBack: (error) => {
        handleUnauthorizedError(error);
      },
    });
  };

  const downloadInvoiceReport = () => {
    setIsDownloadInvoiceReport(true);
    const token = localStorage.getItem("vendorPortalToken");
    const tabVal = tabValue === 0 ? "open" : "close";
    useFetch(API_ENDPOINTS.DOWNLOAD_INVOICES_VP.url, "POST", {
      failureMessage: API_ENDPOINTS.DOWNLOAD_INVOICES_VP.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      data: {
        type: tabVal,
      },
      thenCallBack: (response) => {
        const filename = `Rejected_Invoices_Report_${tabVal}.xlsx`;
        const excelData = Buffer.from(response.data.data, "base64");
        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const blob = new Blob([excelData], { type: fileType });
        saveAs(blob, filename);
        trackInvoiceRejectionDownloaded();
        setIsDownloadInvoiceReport(false);
      },
      catchCallBack: () => {
        setIsDownloadInvoiceReport(false);
      },
    });
  };

  return (
    <>
      <ErrorPage>
        <Suspense fallback={<LoadingPage />}>
          {!loader ? (
            <>
              <Grid sx={{ p: 3, background: "#F9F9F9", minHeight: "100vh" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    padding: "12px 16px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (tabValue === 0) {
                          setRowsDataRejectedInvoicesNonPosted(storeRowsDataRejectedInvoicesNonPosted);
                          setColumnFilters([]);
                          setSelectedQF(RejectedInvoicesQuickFilterOptions.All);
                          uiLogsVendorPortal(uiLoggerNamesVendorPortal.UI_SST_IR_VENDOR_SUMMARY_CLICK.message, {
                            function: uiLoggerNamesVendorPortal.UI_SST_IR_VENDOR_SUMMARY_CLICK.functionName,
                          });
                        }
                      }}
                    >
                      <Typography className="heading-m">Total</Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "#1976d2",
                        }}
                      >
                        <Typography className="heading-m">
                          {formatMoney(ToDineroObj(summaryOfVendorPortal?.totalInvoiceAmount))} (
                          {summaryOfVendorPortal?.totalInvoiceCount})
                        </Typography>
                        {/* <InfoOutlined sx={{ marginLeft: '4px', width: '20px', height: '20px' }} /> */}
                      </Box>
                    </Box>
                    <Box>
                      <Button variant="text" style={{ textTransform: "none" }}>
                        How to use it?
                      </Button>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    {summaryOfVendorPortal?.statusWiseSummary?.map((card, index) => (
                      <Card
                        key={index}
                        sx={{
                          flex: 1,
                          border: "1.18px solid #e4e4e4",
                          borderRadius: "4.74px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setTabValue(0);
                          const issueStatuses =
                            card.title === VendorResolutionStatus.InProgress
                              ? [VendorResolutionStatus.InProgress, VendorResolutionStatus.RejectedAgain]
                              : [card.title];

                          setColumnFilters([]);
                          setSelectedQF(RejectedInvoicesQuickFilterOptions.All);
                          setRowsDataRejectedInvoicesNonPosted(
                            storeRowsDataRejectedInvoicesNonPosted?.filter((row) =>
                              issueStatuses.includes(row.issueStatus)
                            )
                          );
                          uiLogsVendorPortal(uiLoggerNamesVendorPortal.UI_SST_IR_VENDOR_SUMMARY_CLICK.message, {
                            function: uiLoggerNamesVendorPortal.UI_SST_IR_VENDOR_SUMMARY_CLICK.functionName,
                          });
                        }}
                      >
                        <CardContent
                          sx={{
                            padding: "20px !important",
                          }}
                        >
                          <Typography
                            className="heading-xxs"
                            sx={{
                              color: "#717171",
                              marginBottom: "8px",
                            }}
                          >
                            {card.title}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Typography className="heading-s">{formatMoney(ToDineroObj(card.amount))}</Typography>
                            <Typography className="heading-s">({card.count})</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ mt: 2, background: "#fff" }}>
                  <Box
                    sx={{
                      borderBottom: 1,
                      borderColor: "divider",
                      marginTop: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Tabs
                      value={tabValue}
                      onChange={(e, val) => {
                        e.preventDefault();
                        setTabValue(val);
                        trackInvoiceRejectionTabSwitched({ tab: val === 0 ? "Not Posted" : "Posted" });
                        setSelectedQF(RejectedInvoicesQuickFilterOptions.All);

                        setColumnFilters([]);
                      }}
                      sx={{
                        minHeight: 44,
                        "& .MuiTabs-indicator": {
                          backgroundColor: "#531c4c",
                        },
                        "& .MuiChip-root": {
                          color: "#fff",
                          bgcolor: "#0009",
                          px: "6.5px",
                          py: 0,
                        },
                        "& .Mui-selected": {
                          color: "#531c4c !important",
                          "& .MuiChip-root": {
                            bgcolor: "#531c4c",
                          },
                        },
                      }}
                    >
                      <Tab
                        label="Not Posted"
                        icon={<Chip size="small" label={rowsDataRejectedInvoicesNonPosted?.length} />}
                        iconPosition="end"
                        sx={{ textTransform: "none", minHeight: "auto" }}
                      />
                      <Tab
                        label="Posted"
                        icon={<Chip size="small" label={rowsDataRejectedInvoicesPosted?.length} />}
                        iconPosition="end"
                        sx={{ textTransform: "none", minHeight: "auto" }}
                      />
                    </Tabs>

                    <Box>
                      <CustomButton
                        size="small"
                        colorToken="violet"
                        variant="outlined"
                        style={{ marginRight: "16px" }}
                        onClick={() => {
                          downloadInvoiceReport();
                        }}
                        disabled={isDownloadInvoiceReport}
                      >
                        {isDownloadInvoiceReport ? (
                          <CircularProgress size={24} color="inherit" /> // Show loading icon
                        ) : (
                          <Download /> // Show download icon
                        )}
                      </CustomButton>
                    </Box>
                  </Box>

                  <Box>
                    <ColorToggleButton
                      allQFValues={rejectedInvoicesQuickFilterOptions}
                      setSelectedQF={setSelectedQF}
                      setColumnPinning={() => {}}
                      selectedQF={selectedQF}
                    />

                    <ReactTable
                      columns={columnDefinitionRejectedInvoices}
                      rows={finalQuickFilteredRejectedInvoices}
                      loading={loader}
                      onColumnFiltersChange={setColumnFilters}
                      additionalState={{ columnFilters }}
                      enableGrouping={true}
                      positionToolbarAlertBanner={"top"}
                      paginationDisplayMode={"default"}
                      enableRowSelection={false}
                      displayColumnDefOptions={{
                        "mrt-row-actions": {
                          size: 150, //if using layoutMode that is not 'semantic', the columns will not auto-size, so you need to set the size manually
                          grow: false,
                        },
                      }}
                      enableRowActions
                      renderRowActions={({ row }) => {
                        const issueStatus = row.original?.issueStatus; // Get the status of the current row
                        const isViewMode =
                          issueStatus === VendorResolutionStatus.Posted ||
                          issueStatus === VendorResolutionStatus.InvoiceProcessing; // Determine if it's view mode

                        const issueCategory = row.original?.issueCategory; // Get the issue categorization of the current row

                        // Define the allowed issue categorization options
                        const allowedIssueCategorizations = [
                          IssueCategorizationOptions.WRONG_GST_NUMBER,
                          IssueCategorizationOptions.INCORRECT_WRONG_INVOICE_ATTACHED,
                          IssueCategorizationOptions.INVOICE_COPY_NOT_LEGIBLE_VISIBLE,
                          IssueCategorizationOptions.INCOMPLETE_PARTIAL_INVOICE_UPLOADED,
                          IssueCategorizationOptions.INVOICE_COPY_MISSING,
                          IssueCategorizationOptions.INWARDING_ISSUE,
                          IssueCategorizationOptions.WRONG_INWARDING_WRONG_VENDOR_SELECTED,
                          IssueCategorizationOptions.INVOICE_NO_MAX_16_CHARACTER,
                          IssueCategorizationOptions.DUPLICATE_INVOICE,
                          IssueCategorizationOptions.INWARDING_GSTN_MISMATCH,
                          IssueCategorizationOptions.INCORRECT_WRONG_BILLING_ADDRESS,
                        ];

                        // Check if the issue categorization is in the allowed list
                        const isActionAllowed = allowedIssueCategorizations.includes(issueCategory);

                        return (
                          <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px", justifyContent: "center" }}>
                            {isViewMode ? (
                              <CustomButton
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ width: "130px" }}
                                onClick={() => {
                                  console.log(row);
                                  setRejectedInvoicesResolveModalTitle("View Details"); // Set modal title dynamically
                                  selectedRowForRejectedInvoicesResolveModal.current = row.original;
                                  setOpenRejectedInvoicesResolveModal(true);
                                }}
                              >
                                View Details
                              </CustomButton>
                            ) : isActionAllowed ? (
                              <CustomButton
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ width: "130px" }}
                                onClick={() => {
                                  console.log(row);
                                  setRejectedInvoicesResolveModalTitle("Update Details"); // Set modal title dynamically
                                  selectedRowForRejectedInvoicesResolveModal.current = row.original;
                                  setOpenRejectedInvoicesResolveModal(true);
                                  trackInvoiceRejectionTakeActionClicked({
                                    issue_category: row.original.issueCategory,
                                    invoice_number: row.original.invoiceNumber,
                                    grn_amount: row.original.grnAmount,
                                    resolution_status: row.original.issueStatus,
                                  });
                                  uiLogsVendorPortal(
                                    uiLoggerNamesVendorPortal.UI_SST_IR_VENDOR_TAKEACTION_CLICK.message,
                                    {
                                      function:
                                        uiLoggerNamesVendorPortal.UI_SST_IR_VENDOR_TAKEACTION_CLICK.functionName,
                                      vendorCode: row.original.vendorCode,
                                      grnNumber: row.original.grnNumber,
                                      grnStatus: row.original.grnStatus,
                                      vendorName: row.original.vendorName,
                                    }
                                  );
                                }}
                              >
                                Take Action
                              </CustomButton>
                            ) : (
                              <Typography variant="body2" sx={{ width: "130px" }} className="center_align">
                                -
                              </Typography>
                            )}
                          </Box>
                        );
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </>
          ) : (
            <LoadingPage noHeader={true} />
          )}
        </Suspense>
      </ErrorPage>
      {openRejectedInvoicesResolveModal && (
        <VendorPortalRejectedInvoicesResolveModal
          openRejectedInvoicesResolveModal={openRejectedInvoicesResolveModal}
          setOpenRejectedInvoicesResolveModal={setOpenRejectedInvoicesResolveModal}
          selectedRowForRejectedInvoicesResolveModal={selectedRowForRejectedInvoicesResolveModal.current}
          rejectedInvoicesResolveModalTitle={rejectedInvoicesResolveModalTitle}
          listOpenVendorPortalInvoices={listOpenVendorPortalInvoices}
          listCloseVendorPortalInvoices={listCloseVendorPortalInvoices}
          handleUnauthorizedError={handleUnauthorizedError}
          uiLogsVendorPortal={uiLogsVendorPortal}
        />
      )}
    </>
  );
};

export default RejectedInvoicesVendorPortal;
